import React, { useState, useEffect } from 'react';
import { LuRefreshCcw } from 'react-icons/lu';
import AppButton from '@/components/atoms/AppButton';
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
} from '@material-tailwind/react';
import Tooltip from '@/components/atoms/Tooltip';
import FullPageCard from '@/components/organism/Cards/FullPageCard';
import { isEmpty } from 'lodash';
import {
  ROW_PER_PAGE_OPTION,
  pagination as PAGINATION_INITIAL_STATE,
  ALL_LOCATION,
  ALL_OPERATOR,
  STRINGS,
  HTTP_RESPONSES,
} from '@/constant/global/global.constant';
import { DataTable, type ColumnDef } from '@/components/organism/Table';
import { useDispatch, useSelector } from 'react-redux';
import { getReconciliations } from '@/store/slices/reconciliation.slice';
import { getReconciliationsDetail } from '@/store/selectors/reconciliation.selector';
import { getDateTime } from '@/utils/dateTime';
import { tid } from '@/utils/testId';
import Autocomplete from '@/components/elements/AutoComplete';
import {
  RECONCILIATIONS_PROPS,
  RECONCILIATION_DATE_EFILTER,
  RECONCILIATION_REPORTS_RESPONSE,
  RECONCILIATION_TYPES,
  RECONCILIATION_TABLE_PROPS,
} from '@/constant/reconciliation/reconciliation.constant';
import { copyToClipboard } from '@/utils/copyToClipboard';
import { API_URL_CONFIG } from '@/config/urlConfig/apiUrlConfig';
import { axiosInventoryInstance } from '@/config/apiConfig';
import { downloadFile } from '@/utils/generic';
import { Modal } from '@/components/elements/Modal';
import useDeepEffect from '@/hooks/useDeepEffect';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { HiMiniEye } from 'react-icons/hi2';
import { TbEyeCode } from 'react-icons/tb';
import { FaFileDownload } from 'react-icons/fa';
import CommonDropdownSelector from '@/components/organism/GroupDropdown';
import { useDateFilter } from '@/context/dateFilter.context';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import SegmentedControl from '@/components/elements/SegmentedControl';
import { useToast } from '@/components/organism/Toast/ToastProvider';
import { NAV_ITEMS, getNavItemIcon } from '@/constant/layout/layout.constant';
import { APP_BG } from '@/constant/theme/colors.constant';
import {
  ETailwindAlertColor,
  IReconciliationProps,
} from './reconciliation.interface';

const DATE_FILTER_OPTIONS = RECONCILIATION_DATE_EFILTER.map((f) => ({
  value: f.queryParam,
  label: f.name,
}));

const Reconciliation = () => {
  const [isCopy, setIsCopy] = useState(false);
  const [open, setOpen] = React.useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState<any>();
  const [isStateMutated, setIsStateMutated] = useState(false);
  const [dateFilterBy, setDateFilterBy] = useState(
    RECONCILIATION_DATE_EFILTER[0],
  );

  const toast = useToast();

  const handleAction = (item: any, action: string) => {
    if (action === 'DownloadReport' || action === 'ViewReport') {
      const fileURL = `${API_URL_CONFIG.RECONCILIATION.GET_DOWNLOAD_REPORT}/${item?.requestId}`;
      axiosInventoryInstance(fileURL).then((response: any) => {
        if (response.status == HTTP_RESPONSES.SUCCESS.code) {
          if (action === 'ViewReport') {
            setDescription(response.data);
            setIsModalOpen(true);
          } else if (action === 'DownloadReport') {
            downloadFile(
              JSON.stringify(response.data),
              `${item?.requestId}.json`,
              'text/json',
            );
            toast?.pushSuccess(
              RECONCILIATION_REPORTS_RESPONSE.DOWNLOAD.SUCCESS,
              3000,
            );
          }
        } else {
          toast?.pushError(response.message, 3000);
        }
      });
    } else if (action === 'ViewRequest') {
      setDescription(item?.metadata);
      setIsModalOpen(true);
    }
  };
  const handleDateFilterByChange = (value: any) => {
    setDateFilterBy(value);
  };
  const onModalConformation = () => {
    setIsModalOpen(false);
  };
  const [sorting, setSorting] = useState({
    orderBy: 'transactionDtm',
    order: 'desc',
  });
  const [locationDetail, setLocationDetails] = useState({
    location: ALL_LOCATION,
    operator: ALL_OPERATOR,
  });
  const [type, setType] = useState(RECONCILIATION_TYPES[0]);
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const dispatch = useDispatch();
  const { filterValues } = useDateFilter();
  const { startDate, endDate } = filterValues;

  const {
    loading,
    reconciliation: { data, pagination: paginationResponse },
  } = useSelector(getReconciliationsDetail);

  const totalPages = Math.ceil(
    paginationResponse?.totalRecords / pagination.limit,
  );

  const handlePageSizeChange = (value: number) => {
    setPagination({ ...pagination, limit: value });
    if (totalPages < pagination?.page) {
      setPagination({ page: totalPages, limit: value });
    }
  };

  const handlePreviousClick = (page: number) => {
    if (page > 1 && page <= totalPages) {
      setPagination({ ...pagination, page: page - 1 });
    }
  };

  const handleNextClick = (page: number) => {
    if (page >= 1 && page < totalPages) {
      setPagination({ ...pagination, page: page + 1 });
    }
  };

  const handleTypes = (value: any) => {
    setType(value);
  };

  const onCopyClick = () => {
    setIsCopy(true);
    setTimeout(() => {
      setIsCopy(false);
    }, 500);
  };

  const fetchReconciliationDetail = async (param: any) => {
    dispatch(getReconciliations({ ...param }));
  };
  const handleLocationDetails = (val: any) => {
    setLocationDetails({ operator: val?.operator, location: val?.location });
  };

  useEffect(() => {
    if (pagination?.page > totalPages) {
      setPagination({ ...pagination, page: 1 });
    }
  }, [totalPages]);

  useDeepEffect(
    () => {
      setIsStateMutated(true);
    },
    [
      pagination?.limit,
      pagination?.page,
      locationDetail,
      startDate,
      endDate,
      type,
      sorting,
    ],
    true,
  );

  useEffect(() => {
    if (isStateMutated) {
      if (locationDetail?.operator) {
        fetchReconciliationDetail({
          ...pagination,
          startDate,
          endDate,
          ...sorting,
          type: type?.id,
          operatorId: locationDetail?.operator?.id,
          locationId: locationDetail?.location?.locationId,
          dateFilterBy: dateFilterBy?.queryParam,
        });
      }
    }
  }, [
    pagination?.limit,
    pagination?.page,
    locationDetail,
    startDate,
    endDate,
    type,
    sorting,
    dateFilterBy?.queryParam,
  ]);

  const buildColumns = (): ColumnDef<IReconciliationProps>[] => [
    {
      accessor: 'requestId',
      header: RECONCILIATION_TABLE_PROPS.COLUMNS.REPORT_ID,
      render: (item, idx) => (
        <Tooltip id="report_id_tooltip" showToolTips={isCopy} label="Copy">
          <Tooltip content={item?.requestId} placement="right">
            <span
              className="cursor-pointer hover:text-primary transition-colors overflow-hidden whitespace-nowrap w-[120px] inline-block text-ellipsis"
              onClick={() => {
                copyToClipboard(item?.requestId);
                onCopyClick();
              }}
            >
              {item?.requestId}
            </span>
          </Tooltip>
        </Tooltip>
      ),
    },
    {
      accessor: 'requestBy',
      header: RECONCILIATION_TABLE_PROPS.COLUMNS.REQUEST_BY,
      sorting: true,
    },
    {
      accessor: 'reportBy',
      header: RECONCILIATION_TABLE_PROPS.COLUMNS.REPORT_BY,
      sorting: true,
    },
    {
      accessor: 'type',
      header: RECONCILIATION_TABLE_PROPS.COLUMNS.TYPE,
      sorting: true,
      render: (item, idx) => (
        <Tooltip id="type_tooltip" showToolTips={isCopy} label="Copy">
          <Tooltip content={item?.type} placement="right">
            <span
              className="cursor-pointer hover:text-primary transition-colors overflow-hidden whitespace-nowrap w-[120px] inline-block text-ellipsis"
              onClick={() => {
                copyToClipboard(item?.type);
                onCopyClick();
              }}
            >
              {item?.type}
            </span>
          </Tooltip>
        </Tooltip>
      ),
    },
    {
      accessor: 'operatorId',
      header: RECONCILIATION_TABLE_PROPS.COLUMNS.OPERATOR,
      sorting: true,
      render: (item: any) => <span>{item?.operator?.source_id}</span>,
    },
    {
      accessor: 'transactionDtm',
      header: RECONCILIATION_TABLE_PROPS.COLUMNS.TRANSACTION_DTM,
      sorting: true,
      render: (item, idx) => (
        <span>{getDateTime((item as any)?.transactionDtm)}</span>
      ),
    },
    {
      accessor: 'startDate',
      header: RECONCILIATION_TABLE_PROPS.COLUMNS.REQUESTER_DATE_RANGE,
      render: (item, idx) => {
        const reportStartDate = getDateTime((item as any)?.startDate);
        const reportEndDate = getDateTime((item as any)?.endDate);
        const isValidRange = reportStartDate !== '-' && reportEndDate !== '-';
        const displayValue = isValidRange
          ? `${reportStartDate} - ${reportEndDate}`
          : '-';
        return (
          <Tooltip id="date_range_tooltip" showToolTips={isCopy} label="Copy">
            <Tooltip content={displayValue} placement="right">
              <span
                className="cursor-pointer hover:text-primary transition-colors overflow-hidden whitespace-nowrap w-[150px] inline-block text-ellipsis"
                onClick={() => {
                  copyToClipboard(displayValue);
                  onCopyClick();
                }}
              >
                {displayValue}
              </span>
            </Tooltip>
          </Tooltip>
        );
      },
    },
    {
      accessor: 'path',
      header: RECONCILIATION_TABLE_PROPS.COLUMNS.ACTIONS,
      render: (item: any) => (
        <Menu placement="left">
          <MenuHandler>
            <AppButton
              variant="neutral"
              shape="square"
              size="xs"
              icon={<BsThreeDotsVertical className="w-4 h-4" />}
            />
          </MenuHandler>
          <MenuList
            placeholder=""
            onResize={undefined}
            onResizeCapture={undefined}
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          >
            <MenuItem
              placeholder=""
              onResize={undefined}
              onResizeCapture={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              onClick={() => {
                setTitle(RECONCILIATION_TABLE_PROPS.MENU.VIEW_REQUEST_TITLE);
                handleAction(item, 'ViewRequest');
              }}
              className="flex items-center gap-2"
            >
              <HiMiniEye className="h-4 w-4" />{' '}
              {RECONCILIATION_TABLE_PROPS.MENU.VIEW_REQUEST_LABEL}
            </MenuItem>
            <MenuItem
              placeholder=""
              onResize={undefined}
              onResizeCapture={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              onClick={() => {
                setTitle(RECONCILIATION_TABLE_PROPS.MENU.VIEW_REPORT_TITLE);
                handleAction(item, 'ViewReport');
              }}
              className="flex items-center gap-2"
            >
              <TbEyeCode className="h-4 w-4" />{' '}
              {RECONCILIATION_TABLE_PROPS.MENU.VIEW_REPORT_LABEL}
            </MenuItem>
            <MenuItem
              placeholder=""
              onResize={undefined}
              onResizeCapture={undefined}
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
              onClick={() => {
                setTitle(RECONCILIATION_TABLE_PROPS.MENU.DOWNLOAD_REPORT_TITLE);
                handleAction(item, 'DownloadReport');
              }}
              className="flex items-center gap-2"
            >
              <FaFileDownload className="h-4 w-4" />{' '}
              {RECONCILIATION_TABLE_PROPS.MENU.DOWNLOAD_REPORT_LABEL}
            </MenuItem>
          </MenuList>
        </Menu>
      ),
    },
  ];

  return (
    <FullPageCard
      className="rounded-b-none"
      header={
        <PageToolbar
          title={STRINGS.RECONCI}
          subtitle={STRINGS.RECONCI_SUB}
          icon={getNavItemIcon(NAV_ITEMS.RECONCILIATION)}
          count={paginationResponse?.totalRecords}
          filtersLeft={
            <CommonDropdownSelector
              onSelectionChange={handleLocationDetails}
              showOperator
              showLocation
              data-testid={tid('reconciliation-operator-location-dropdown')}
            />
          }
          filtersRight={
            <div className="flex items-center gap-2">
              <SegmentedControl
                options={DATE_FILTER_OPTIONS}
                value={dateFilterBy.queryParam}
                onChange={(v) => {
                  const found = RECONCILIATION_DATE_EFILTER.find(
                    (f) => f.queryParam === v,
                  );
                  if (found) setDateFilterBy(found);
                }}
                size="xs"
                shape="square"
                variant="gradient"
              />
              <Autocomplete
                label={RECONCILIATIONS_PROPS.RECONCILIATIONS.TYPE}
                placeholder={RECONCILIATIONS_PROPS.RECONCILIATIONS.SELECT_TYPE}
                selectedValue={type}
                handleSelectedValue={handleTypes}
                handleSearch={setType}
                lists={RECONCILIATION_TYPES}
              />
            </div>
          }
        />
      }
    >
      <DataTable<IReconciliationProps>
        data-testid={tid('reconciliation-table')}
        data={data as unknown as IReconciliationProps[]}
        loading={loading}
        skeletonRows={15}
        columns={buildColumns()}
        onSort={(orderBy: any, order: any) => setSorting({ orderBy, order })}
        sortState={sorting as any}
        pagination={
          !isEmpty(data)
            ? {
              state: pagination,
              totalItem: paginationResponse?.totalRecords,
              rowsPerPageOptions: ROW_PER_PAGE_OPTION,
              onRowsPerPageChange: handlePageSizeChange,
              onNextClick: handleNextClick,
              onPreviousClick: handlePreviousClick,
            }
            : null
        }
        rowKey={(item) => (item as any)?.requestId ?? Math.random().toString()}
      />

      <Modal
        open={isModalOpen}
        onClose={onModalConformation}
        variant="default"
        size="xl"
      >
        <Modal.Header title={title} />
        <Modal.Body>
          <pre
            className={`text-xs ${APP_BG.canvas} border border-gray-200 dark:border-gray-700 rounded-lg p-4 overflow-auto max-h-[60vh] whitespace-pre-wrap break-all text-gray-700 dark:text-gray-300`}
          >
            <code>{JSON.stringify(description, null, 2)}</code>
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Modal.CancelButton onClick={onModalConformation}>
            {RECONCILIATION_TABLE_PROPS.MODAL.CLOSE_BUTTON}
          </Modal.CancelButton>
        </Modal.Footer>
      </Modal>
    </FullPageCard>
  );
};
export default Reconciliation;
